import { useState } from "react";
import { Link } from "react-router-dom";
import { DeviceMobile, Export, PlusSquare, Truck, Package, Camera, Moon, ShieldCheck, Wallet, Copy, Check } from "@phosphor-icons/react";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const SHOTS = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/ca80d7df37a224548ae2d21efac2ed173c91550901fc62c2b7bc21214429b1fa.jpeg",
    caption: "In the cab: your load, your window, your paperwork — one thumb.",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/7767a77a00d0d53790a4c36e7b144a1a9f590efc368282de5942fb7d4b73d151.jpeg",
    caption: "On the floor: count stock, scan a bin, book a tool out.",
  },
];

const SOCIAL_TILE = "https://static.prod-images.emergentagent.com/jobs/816db716-8135-404c-89b1-f60cefaf7f84/images/571746db0fc5dcca7a0063ab9405d88024b82addef0c5d712794427333bc808c.jpeg";

const DRIVERS = [
  [Truck, "Your run, live", "Promised windows, pick-up and delivery status, proof photos and the tracking number — updated from the cab."],
  [Moon, "Rest that counts", "Log sleep and breaks, find a motel near the yard or on the route, and book it at the commission rate."],
  [Camera, "One-tap share", "Post a photo from the yard and share it to WhatsApp, Facebook, X or LinkedIn without leaving the page."],
];

const MANAGERS = [
  [Package, "Stock in your pocket", "Adjust stock, check reorder levels, scan a bin and see every site's holdings."],
  [ShieldCheck, "Approve on the move", "Purchase orders, quotes, insurance offers and incident reports — read and decide from the phone."],
  [Wallet, "Money, settled", "Pay a purchase order or a freight charge with Apple Pay through Stripe Checkout."],
];

const CAPTIONS = [
  {
    key: "facebook",
    label: "Facebook",
    text: "Our whole yard now runs from a phone. Stock, tools, freight windows, safety reports and payments — one login, no app store, no training day. Add it to your iPhone home screen and it opens full screen like any other app. Unity & Utility.",
  },
  {
    key: "instagram",
    label: "Instagram",
    text: "The floor in your pocket. Stock counts, tool crib, freight windows, rest breaks, payments — one login on your iPhone.\n\n#Logistics #Manufacturing #Freight #Warehouse #FleetManagement #UnityAndUtility",
  },
  {
    key: "x",
    label: "X",
    text: "Runs on your iPhone: stock, tool crib, freight windows, safety, rest logs and payments. One login, add to home screen, done. No app store.",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    text: "Most of our users are not at a desk — they are in a cab, on a forklift or on a night shift. So Unity & Utility is built mobile-first: inventory, purchasing, freight time windows, safety reporting, wellbeing and payments all work one-handed on an iPhone, installed straight from Safari with no app store queue.",
  },
];

const Group = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <h3 className="font-display text-lg font-bold tracking-tight text-[#0F172A]">{title}</h3>
    <div className="mt-3 grid gap-3 sm:grid-cols-3">
      {items.map(([Icon, head, body]) => (
        <div key={head} className="border border-border bg-white p-4">
          <Icon size={22} className="text-[#002FA7]" weight="duotone" />
          <p className="mt-2 text-sm font-semibold text-[#0F172A]">{head}</p>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{body}</p>
        </div>
      ))}
    </div>
  </div>
);

export default function Iphone() {
  const [copied, setCopied] = useState(null);

  useSeo({
    title: "Unity & Utility on your iPhone — install it from Safari | Unity & Utility",
    description: "Run stock, tools, purchasing, freight windows, safety, rest and payments from an iPhone. Add it to your home screen from Safari — full screen, no app store.",
    path: "/iphone",
    image: SOCIAL_TILE,
    jsonLd: breadcrumbs([["Home", "/"], ["iPhone", "/iphone"]]),
  });

  const copy = async (key, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 2000); } catch {}
  };

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="iphone-page">
        <Crumbs items={[["Home", "/"], ["iPhone", "/iphone"]]} />
        <p className="overline text-[#B3241B]">Built for the phone in your pocket</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
          <DeviceMobile size={36} weight="duotone" className="mt-1 shrink-0 text-[#002FA7]" />
          Unity &amp; Utility on your iPhone
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="iphone-intro">
          Most of the people who use this are not sat at a desk — they are in a cab, on a forklift or halfway
          through a night shift. So every screen works one-handed on an iPhone, and Safari can install it on
          your home screen in three taps. No app store, no download, no training day.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="iphone-actions">
          <Link to="/register" data-testid="iphone-cta-register"
            className="bg-[#002FA7] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-150 hover:bg-[#00248A]">
            Create an account
          </Link>
          <Link to="/login" data-testid="iphone-cta-login" className="tap-safe text-sm font-semibold text-[#002FA7] hover:underline">
            Already have one? Sign in
          </Link>
        </div>
        <div className="mt-4">
          <ShareBar url={`${window.location.origin}/iphone`}
            text="Unity & Utility runs on your iPhone — stock, tools, freight, safety and payments, installed straight from Safari."
            testid="iphone-share" instagram />
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="iphone-shots">
          {SHOTS.map((s) => (
            <figure key={s.src} className="border border-border bg-white">
              <img src={s.src} alt={s.caption} loading="lazy" className="aspect-[3/4] w-full object-cover" />
              <figcaption className="border-t border-border p-3 text-sm text-slate-600">{s.caption}</figcaption>
            </figure>
          ))}
        </div>

        <section className="mt-12 border border-[#002FA7] bg-white p-5 sm:p-7" data-testid="iphone-install">
          <p className="overline text-[#002FA7]">Three taps in Safari</p>
          <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Put it on your home screen
          </h2>
          <ol className="mt-4 space-y-4">
            {[
              [Export, "Open this site in Safari and tap the Share button at the bottom of the screen."],
              [PlusSquare, "Scroll the share sheet and choose “Add to Home Screen”."],
              [DeviceMobile, "Name it Unity and tap Add. It now opens full screen, with no browser bars."],
            ].map(([Icon, text], i) => (
              <li key={i} className="flex gap-3" data-testid={`install-step-${i + 1}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-[#0F172A] text-sm font-bold text-white">{i + 1}</span>
                <p className="pt-1 text-sm leading-relaxed text-slate-700">
                  <Icon size={16} className="mr-1.5 inline align-[-3px] text-[#002FA7]" />
                  {text}
                </p>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-slate-500" data-testid="iphone-no-appstore">
            This is the web app installed on your phone, not an App Store download — so you always have the
            current version and nothing to update.
          </p>
        </section>

        <div className="mt-12 space-y-8">
          <Group title="If you drive or work the floor" items={DRIVERS} testid="iphone-drivers" />
          <Group title="If you buy, sell or run the place" items={MANAGERS} testid="iphone-managers" />
        </div>

        <section className="mt-12" data-testid="iphone-promo">
          <p className="overline text-[#B3241B]">Tell your crew and your customers</p>
          <h2 className="font-display mt-1 text-base font-bold tracking-tight text-[#0F172A] md:text-lg">
            Ready-made posts
          </h2>
          <img src={SOCIAL_TILE} alt="Unity & Utility on iPhone" loading="lazy"
            className="mt-4 w-full border border-border object-cover" data-testid="iphone-promo-image" />
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {CAPTIONS.map((c) => (
              <div key={c.key} className="border border-border bg-white p-4" data-testid={`iphone-caption-${c.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="overline text-slate-500">{c.label}</p>
                  <button type="button" data-testid={`copy-caption-${c.key}`} onClick={() => copy(c.key, c.text)}
                    className="tap-safe text-xs font-semibold text-[#002FA7]">
                    {copied === c.key
                      ? <><Check size={13} className="mr-1 inline align-[-2px]" />Copied</>
                      : <><Copy size={13} className="mr-1 inline align-[-2px]" />Copy</>}
                  </button>
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">{c.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Save the picture above, copy a caption and post it — Instagram and Facebook do not allow posting on
            your behalf without a reviewed business app.
          </p>
        </section>
      </div>
    </PublicShell>
  );
}
